import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, TouchableOpacity, ActivityIndicator, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { apiService, FlowRunItem, FlowRunDetail } from '../services/api';
import { FlowRunStepPicker } from './flows/FlowRunStepPicker';
import { NODE_TYPE_META } from './flows/flow-types';
import { useAppContext } from '../contexts/AppContext';
import { useLocale } from '../contexts/LocaleContext';
import { useFlowPermissions } from '../hooks/useFlowPermissions';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { colors, spacing, typography, withAlpha } from '../theme';
import { designTokens } from '../theme/design-system';

/**
 * Contact profile → Workflow runs: this contact's runs of every flow, newest first. Open one for
 * where it is and its last steps; stop it, resume a paused one, or continue it from any step of
 * its flow (it goes on from there in its conversation). "All runs" opens Flow runs filtered to
 * the contact.
 */

const PAGE = 5;
const STATUS_COLOR: Record<FlowRunItem['status'], string> = {
  active: '#2563EB', waiting: '#D97706', paused: '#7C3AED', completed: '#16A34A', stopped: '#6B7280',
};
const isLive = (status: FlowRunItem['status']) => status === 'active' || status === 'waiting' || status === 'paused';
const formatDate = (iso?: string) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' } as any) : '—');
const errorOf = (res: any, fallback: string) => res?.errors?.[0]?.message || res?.errors?.[0] || fallback;

export function ContactFlowRuns({ contactId }: { contactId: string }) {
  const styles = useThemedStyles(getStyles);
  const { t } = useLocale();
  const rtl = useRTLStyles();
  const { navigateTo, showNotification } = useAppContext();
  const { canView, canEdit } = useFlowPermissions();
  const [runs, setRuns] = useState<FlowRunItem[] | null>(null);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(PAGE);
  const [openId, setOpenId] = useState<string | null>(null);
  const [detail, setDetail] = useState<FlowRunDetail | null>(null);
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!canView || !contactId) return;
    let alive = true;
    apiService.getFlowRuns({ contactId, limit }).then((res) => {
      if (!alive) return;
      if (res.statusCode === 1000 && res.data) { setRuns(res.data.items); setTotal(res.data.total); } else setRuns([]);
    }).catch(() => alive && setRuns([]));
    return () => { alive = false; };
  }, [contactId, limit, reloadKey, canView]);

  useEffect(() => {
    setDetail(null);
    if (!openId) return;
    let alive = true;
    apiService.getFlowRun(openId).then((res) => { if (alive && res.statusCode === 1000 && res.data) setDetail(res.data); }).catch(() => undefined);
    return () => { alive = false; };
  }, [openId, reloadKey]);

  // A moved / resumed run keeps going in the background: look again shortly after.
  const reloadSoon = useCallback(() => {
    setReloadKey((k) => k + 1);
    setTimeout(() => setReloadKey((k) => k + 1), 2500);
  }, []);

  const act = async (runId: string, action: 'resume' | 'stop' | 'move', nodeId?: string) => {
    setBusy(true);
    try {
      const res = action === 'resume' ? await apiService.resumeFlowSession(runId)
        : action === 'stop' ? await apiService.stopFlowSession(runId)
          : await apiService.moveFlowSession(runId, nodeId!);
      if (res.statusCode !== 1000) throw new Error(errorOf(res, t('flowRuns.actionFailed')));
      setPicking(false);
      showNotification('success', t('common.success'), action === 'stop' ? t('flowRuns.stopped') : action === 'move' ? t('flowRuns.moved') : t('flowRuns.resumed'));
      reloadSoon();
    } catch (error: any) {
      showNotification('error', t('common.error'), error?.message || t('flowRuns.actionFailed'));
    } finally {
      setBusy(false);
    }
  };

  if (!canView) return null;

  return (
    <View style={styles.card}>
      <View style={[styles.header, { flexDirection: rtl.rowDirection }]}>
        <Text style={[styles.title, { textAlign: rtl.textStart }]}>{t('flowRuns.contact.title')}</Text>
        {total > 0 && (
          <TouchableOpacity onPress={() => navigateTo('dashboard-flows-all', undefined, { ref: contactId })} accessibilityRole="link">
            <Text style={styles.link}>{t('flowRuns.contact.all', { count: total })}</Text>
          </TouchableOpacity>
        )}
      </View>
      {runs === null ? <ActivityIndicator color={colors.primary as string} /> : runs.length === 0 ? (
        <Text style={[styles.empty, { textAlign: rtl.textStart }]}>{t('flowRuns.contact.empty')}</Text>
      ) : runs.map((run) => {
        const open = openId === run.id;
        const live = isLive(run.status);
        const lastMeta = NODE_TYPE_META.find((m) => m.type === run.lastStep?.nodeType);
        return (
          <View key={run.id} style={[styles.run, open && styles.runOpen]}>
            <TouchableOpacity style={[styles.runRow, { flexDirection: rtl.rowDirection }]} onPress={() => setOpenId(open ? null : run.id)} accessibilityRole="button">
              <View style={[styles.dot, { backgroundColor: STATUS_COLOR[run.status] }]} />
              <View style={{ flex: 1 }}>
                <Text style={[styles.flowName, { textAlign: rtl.textStart }]} numberOfLines={1}>{run.flowName}</Text>
                <Text style={[styles.meta, { textAlign: rtl.textStart }]} numberOfLines={1}>
                  {t(`flowRuns.status.${run.status}`)} · {formatDate(run.startedAt)}
                  {run.lastStep ? ` · ${run.lastStep.nodeName || lastMeta?.label || run.lastStep.nodeType}` : ''}
                  {run.errorCount ? ` · ${t('flowRuns.errors', { count: run.errorCount })}` : ''}
                </Text>
              </View>
              <Ionicons name={open ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary as string} />
            </TouchableOpacity>
            {open && (
              <View style={styles.detail}>
                {!detail ? <ActivityIndicator color={colors.primary as string} /> : (
                  <>
                    {live && <Text style={[styles.meta, { textAlign: rtl.textStart }]}>{t('flowRuns.nowAt', { step: detail.currentNode?.name || detail.currentNode?.id || '—' })}</Text>}
                    {detail.status === 'waiting' && !!detail.waitUntil && <Text style={[styles.meta, { textAlign: rtl.textStart }]}>{t('flowRuns.waitingUntil', { date: formatDate(detail.waitUntil) })}</Text>}
                    {detail.steps.slice(-5).map((step, i) => (
                      <Text key={`${step.nodeId}-${i}`} style={[styles.step, { textAlign: rtl.textStart }, step.outcome === 'error' && { color: colors.error as string }]} numberOfLines={2}>
                        • {step.nodeName || NODE_TYPE_META.find((m) => m.type === step.nodeType)?.label || step.nodeType}{step.path ? ` → ${step.path}` : ''}{step.detail ? ` — ${step.detail}` : ''}
                      </Text>
                    ))}
                    <View style={[styles.actions, { flexDirection: rtl.rowDirection }]}>
                      {!!detail.conversationId && (
                        <TouchableOpacity style={styles.button} onPress={() => navigateTo('conversation', detail.conversationId)}>
                          <Ionicons name="chatbubbles-outline" size={14} color={colors.primary as string} />
                          <Text style={styles.buttonText}>{t('flowRuns.openConversation')}</Text>
                        </TouchableOpacity>
                      )}
                      <TouchableOpacity style={styles.button} onPress={() => navigateTo('flow-edit', detail.flowId, { runId: detail.id })}>
                        <Ionicons name="git-network-outline" size={14} color={colors.primary as string} />
                        <Text style={styles.buttonText}>{t('flowRuns.showPath')}</Text>
                      </TouchableOpacity>
                      {canEdit && !!detail.conversationId && !!detail.nodes?.length && (
                        <TouchableOpacity style={styles.button} disabled={busy} onPress={() => setPicking(true)}>
                          <Ionicons name="play-skip-forward-outline" size={14} color={colors.primary as string} />
                          <Text style={styles.buttonText}>{t('flowRuns.move.action')}</Text>
                        </TouchableOpacity>
                      )}
                      {canEdit && detail.status === 'paused' && (
                        <TouchableOpacity style={[styles.button, styles.primary]} disabled={busy} onPress={() => act(detail.id, 'resume')}>
                          <Ionicons name="play" size={14} color="white" />
                          <Text style={[styles.buttonText, { color: 'white' }]}>{t('flowRuns.resume')}</Text>
                        </TouchableOpacity>
                      )}
                      {canEdit && isLive(detail.status) && (
                        <TouchableOpacity style={styles.button} disabled={busy} onPress={() => act(detail.id, 'stop')}>
                          <Ionicons name="stop" size={14} color={colors.error as string} />
                          <Text style={[styles.buttonText, { color: colors.error as string }]}>{t('flowRuns.stop')}</Text>
                        </TouchableOpacity>
                      )}
                    </View>
                    <FlowRunStepPicker
                      visible={picking}
                      nodes={detail.nodes || []}
                      currentNodeId={live ? detail.currentNode?.id : undefined}
                      busy={busy}
                      onClose={() => setPicking(false)}
                      onPick={(nodeId) => act(detail.id, 'move', nodeId)}
                    />
                  </>
                )}
              </View>
            )}
          </View>
        );
      })}
      {runs && total > runs.length && (
        <TouchableOpacity onPress={() => setLimit((l) => l + PAGE)}>
          <Text style={[styles.link, { textAlign: 'center' }]}>{t('flowRuns.contact.more')}</Text>
        </TouchableOpacity>
      )}
    </View>
  );
}

const getStyles = () => StyleSheet.create({
  card: { gap: spacing.xs },
  header: { alignItems: 'center', justifyContent: 'space-between' },
  title: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.text.primary },
  link: { fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.semibold, color: colors.primary },
  empty: { fontSize: typography.fontSize.sm, color: colors.text.secondary },
  run: { borderWidth: 1, borderColor: colors.border, borderRadius: designTokens.borderRadius.md, backgroundColor: colors.background.surface },
  runOpen: { borderColor: withAlpha(colors.primary as string, 0.4) },
  runRow: { alignItems: 'center', gap: spacing.sm, padding: spacing.sm },
  dot: { width: 8, height: 8, borderRadius: 4 },
  flowName: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.medium, color: colors.text.primary },
  meta: { fontSize: typography.fontSize.xs, color: colors.text.secondary },
  detail: { paddingHorizontal: spacing.sm, paddingBottom: spacing.sm, gap: 4 },
  step: { fontSize: typography.fontSize.xs, color: colors.text.primary },
  actions: { flexWrap: 'wrap', gap: spacing.xs, marginTop: spacing.xs },
  button: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: spacing.sm, paddingVertical: 6, borderRadius: designTokens.borderRadius.sm, borderWidth: 1, borderColor: colors.border },
  primary: { backgroundColor: colors.primary, borderColor: colors.primary },
  buttonText: { fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.semibold, color: colors.primary },
});
